import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ClipboardList, UserSearch, BarChart3, TrendingUp, Users, Star, Euro, CheckCircle } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const ClientMystere = () => {
  const { t } = useLanguage();

  return (
    <div data-testid="client-mystere-page">
      {/* Hero */}
      <section className="page-header page-header-professionnels" data-testid="cm-header">
        <p className="text-sm uppercase tracking-widest text-[#C9A227] mb-4 font-sans animate-fade-in">{t('cm.tagline')}</p>
        <h1 className="font-serif text-4xl md:text-6xl text-white mb-6 animate-fade-in-delay-1">
          {t('cm.heroTitle')}
        </h1>
        <div className="gold-line gold-line-center mb-6 animate-fade-in-delay-2"></div>
        <p className="text-white/80 text-lg max-w-2xl text-center animate-fade-in-delay-3">
          {t('cm.heroSubtitle')}
        </p>
      </section>

      {/* Process - 3 Steps */}
      <section className="section-luxury bg-[#1A1A1A]" data-testid="cm-process">
        <div className="container-luxury">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <p className="text-sm uppercase tracking-widest text-[#C9A227] mb-4 font-sans">{t('cm.simpleProcess')}</p>
            <h2 className="font-serif text-3xl md:text-4xl text-white mb-6">{t('cm.howItWorks')}</h2>
            <div className="gold-line gold-line-center"></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center" data-testid="cm-step-1">
              <div className="w-20 h-20 rounded-full bg-[#C9A227] flex items-center justify-center mx-auto mb-6">
                <ClipboardList className="w-10 h-10 text-white" />
              </div>
              <span className="text-[#C9A227] text-sm uppercase tracking-widest">01</span>
              <h3 className="font-serif text-2xl text-white mt-2 mb-4">{t('cm.step1Title')}</h3>
              <p className="text-white/60 leading-relaxed">{t('cm.step1Desc')}</p>
            </div>
            <div className="text-center" data-testid="cm-step-2">
              <div className="w-20 h-20 rounded-full bg-[#C9A227] flex items-center justify-center mx-auto mb-6">
                <UserSearch className="w-10 h-10 text-white" />
              </div>
              <span className="text-[#C9A227] text-sm uppercase tracking-widest">02</span>
              <h3 className="font-serif text-2xl text-white mt-2 mb-4">{t('cm.step2Title')}</h3>
              <p className="text-white/60 leading-relaxed">{t('cm.step2Desc')}</p>
            </div>
            <div className="text-center" data-testid="cm-step-3">
              <div className="w-20 h-20 rounded-full bg-[#C9A227] flex items-center justify-center mx-auto mb-6">
                <BarChart3 className="w-10 h-10 text-white" />
              </div>
              <span className="text-[#C9A227] text-sm uppercase tracking-widest">03</span>
              <h3 className="font-serif text-2xl text-white mt-2 mb-4">{t('cm.step3Title')}</h3>
              <p className="text-white/60 leading-relaxed">{t('cm.step3Desc')}</p>
            </div>
          </div>
          <div className="text-center mt-12">
            <Link to="/professionnels/client-mystere/tarifs" className="btn-primary inline-flex items-center gap-2" data-testid="cm-process-pricing-btn">
              {t('cm.seePricing')} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* 4 Objectives */}
      <section className="section-luxury bg-[#FDFCF8] texture-overlay" data-testid="cm-objectives">
        <div className="container-luxury">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <p className="text-sm uppercase tracking-widest text-[#C9A227] mb-4 font-sans">{t('cm.objectivesTag')}</p>
            <h2 className="font-serif text-3xl md:text-4xl text-[#1A1A1A] mb-6">{t('cm.objectivesTitle')}</h2>
            <div className="gold-line gold-line-center"></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="service-card group" data-testid="cm-obj-revenue">
              <div className="flex items-start gap-6">
                <div className="w-16 h-16 rounded-full border border-[#C9A227] flex items-center justify-center flex-shrink-0 group-hover:bg-[#C9A227] transition-all duration-500">
                  <TrendingUp className="w-8 h-8 text-[#C9A227] group-hover:text-white transition-colors duration-500" />
                </div>
                <div>
                  <h3 className="font-serif text-xl text-[#1A1A1A] mb-3">{t('cm.obj1Title')}</h3>
                  <p className="text-gray-600 leading-relaxed">{t('cm.obj1Desc')}</p>
                </div>
              </div>
            </div>
            <div className="service-card group" data-testid="cm-obj-teams">
              <div className="flex items-start gap-6">
                <div className="w-16 h-16 rounded-full border border-[#C9A227] flex items-center justify-center flex-shrink-0 group-hover:bg-[#C9A227] transition-all duration-500">
                  <Users className="w-8 h-8 text-[#C9A227] group-hover:text-white transition-colors duration-500" />
                </div>
                <div>
                  <h3 className="font-serif text-xl text-[#1A1A1A] mb-3">{t('cm.obj2Title')}</h3>
                  <p className="text-gray-600 leading-relaxed">{t('cm.obj2Desc')}</p>
                </div>
              </div>
            </div>
            <div className="service-card group" data-testid="cm-obj-reputation">
              <div className="flex items-start gap-6">
                <div className="w-16 h-16 rounded-full border border-[#C9A227] flex items-center justify-center flex-shrink-0 group-hover:bg-[#C9A227] transition-all duration-500">
                  <Star className="w-8 h-8 text-[#C9A227] group-hover:text-white transition-colors duration-500" />
                </div>
                <div>
                  <h3 className="font-serif text-xl text-[#1A1A1A] mb-3">{t('cm.obj3Title')}</h3>
                  <p className="text-gray-600 leading-relaxed">{t('cm.obj3Desc')}</p>
                </div>
              </div>
            </div>
            <div className="service-card group" data-testid="cm-obj-cash">
              <div className="flex items-start gap-6">
                <div className="w-16 h-16 rounded-full border border-[#C9A227] flex items-center justify-center flex-shrink-0 group-hover:bg-[#C9A227] transition-all duration-500">
                  <Euro className="w-8 h-8 text-[#C9A227] group-hover:text-white transition-colors duration-500" />
                </div>
                <div>
                  <h3 className="font-serif text-xl text-[#1A1A1A] mb-3">{t('cm.obj4Title')}</h3>
                  <p className="text-gray-600 leading-relaxed">{t('cm.obj4Desc')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pack Inclusions */}
      <section className="section-luxury bg-white" data-testid="cm-pack">
        <div className="container-luxury">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-sm uppercase tracking-widest text-[#C9A227] mb-4 font-sans">{t('cm.packTag')}</p>
              <h2 className="font-serif text-3xl md:text-4xl text-[#1A1A1A] mb-6">{t('cm.packTitle')}</h2>
              <div className="gold-line mb-8"></div>
              <p className="text-gray-600 text-lg leading-relaxed mb-8">{t('cm.packIntro')}</p>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-[#C9A227] flex-shrink-0 mt-0.5" />
                  <span className="text-gray-600">{t('cm.packItem1')}</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-[#C9A227] flex-shrink-0 mt-0.5" />
                  <span className="text-gray-600">{t('cm.packItem2')}</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-[#C9A227] flex-shrink-0 mt-0.5" />
                  <span className="text-gray-600">{t('cm.packItem3')}</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-[#C9A227] flex-shrink-0 mt-0.5" />
                  <span className="text-gray-600">{t('cm.packItem4')}</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-[#C9A227] flex-shrink-0 mt-0.5" />
                  <span className="text-gray-600">{t('cm.packItem5')}</span>
                </li>
              </ul>
            </div>
            <div className="p-8 bg-[#FDFCF8] border border-[#F5EFD3]">
              <p className="text-gray-600 text-lg leading-relaxed">{t('cm.packIntro')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-luxury bg-[#1A1A1A]" data-testid="cm-cta">
        <div className="container-luxury text-center max-w-3xl mx-auto">
          <h2 className="font-serif text-3xl md:text-4xl text-white mb-6">{t('cm.ctaTitle')}</h2>
          <div className="gold-line gold-line-center mb-8"></div>
          <p className="text-white/70 text-lg mb-10">{t('cm.ctaText')}</p>
          <Link to="/professionnels/client-mystere/tarifs" className="btn-primary inline-flex items-center gap-2" data-testid="cm-cta-pricing">
            {t('cm.seePricing')} <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ClientMystere;
